import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/profile-components/Navbar";
import Topbar from "../../components/profile-components/Topbar";
import API_BASE_URL from "../../config";
import "../SalesPages.css";
import "../Settings.css";
import "../../styles/Tabs.css";
import "./PolicyStatus.css";

const PolicyStatus = () => {
  const [statuses, setStatuses] = useState([]);
  const [name, setName] = useState("");
  const [type, setType] = useState("policy");
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("token");

  const filteredStatuses = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return statuses;

    return statuses.filter((status) =>
      String(status.name || "").toLowerCase().includes(term)
    );
  }, [statuses, searchTerm]);

  const policyStatuses = useMemo(() => {
    return filteredStatuses.filter((status) => status.type === "policy");
  }, [filteredStatuses]);

  const opportunityStatuses = useMemo(() => {
    return filteredStatuses.filter((status) => status.type === "opportunity");
  }, [filteredStatuses]);

  const namesToAdd = useMemo(() => {
    return name
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);
  }, [name]);

  useEffect(() => {
    loadStatuses();
  }, []);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const loadStatuses = async () => {
    try {
      setLoading(true);
      clearMessages();

      const res = await fetch(`${API_BASE_URL}/api/policy-statuses`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to load statuses.");
      }

      setStatuses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching statuses:", err);
      setError(err.message || "Failed to load statuses.");
      setSuccess("");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!namesToAdd.length) {
      setError("Please enter at least one status name.");
      setSuccess("");
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      const addedStatuses = [];

      for (const statusName of namesToAdd) {
        const res = await fetch(`${API_BASE_URL}/api/policy-statuses`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: statusName,
            type,
          }),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data.error || data.message || `Failed to add ${statusName}.`);
        }

        addedStatuses.push({
          id: data.id,
          name: data.name || statusName,
          type: data.type || type,
          subscriber_id: data.subscriber_id,
          is_default: data.is_default,
        });
      }

      setName("");
      setSuccess(`${addedStatuses.length} status(es) added successfully.`);
      loadStatuses();
    } catch (err) {
      console.error("Error adding statuses:", err);
      setError(err.message || "Failed to add status(es).");
      setSuccess("");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedStatus) return;

    const cleanName = editedName.trim();

    if (!cleanName) {
      setError("Status name is required.");
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      const res = await fetch(`${API_BASE_URL}/api/policy-statuses/${selectedStatus.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: cleanName }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to update status.");
      }

      setStatuses((prev) =>
        prev.map((status) =>
          status.id === selectedStatus.id ? { ...status, name: cleanName } : status
        )
      );

      setSelectedStatus(null);
      setEditedName("");
      setSuccess("Status updated successfully.");
    } catch (err) {
      console.error("Status update error:", err);
      setError(err.message || "Update failed.");
      setSuccess("");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedStatus) return;

    if (!window.confirm(`Delete ${selectedStatus.name}?`)) return;

    try {
      setSaving(true);
      clearMessages();

      const res = await fetch(`${API_BASE_URL}/api/policy-statuses/${selectedStatus.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to delete status.");
      }

      setStatuses((prev) => prev.filter((status) => status.id !== selectedStatus.id));
      setSelectedStatus(null);
      setEditedName("");
      setSuccess("Status deleted successfully.");
    } catch (err) {
      console.error("Status delete error:", err);
      setError(err.message || "Delete failed.");
      setSuccess("");
    } finally {
      setSaving(false);
    }
  };

  const canEditStatus = (status) => Boolean(status.subscriber_id);

  const renderStatusTable = (items, emptyMessage) => {
    if (items.length > 0) {
      return (
        <table className="policy-status-table">
          <thead>
            <tr>
              <th>Status Name</th>
              <th>Scope</th>
              <th className="policy-status-table-action-col">Action</th>
            </tr>
          </thead>

          <tbody>
            {items.map((status) => (
              <tr
                key={status.id}
                onClick={() => {
                  if (!canEditStatus(status)) return;
                  setSelectedStatus(status);
                  setEditedName(status.name || "");
                }}
              >
                <td>{status.name}</td>

                <td>
                  {status.subscriber_id ? (
                    <span className="policy-status-pill custom">Custom</span>
                  ) : (
                    <span className="policy-status-pill default">Default</span>
                  )}
                </td>

                <td className="policy-status-table-action-col">
                  {canEditStatus(status) ? (
                    <button
                      type="button"
                      className="policy-status-table-edit-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedStatus(status);
                        setEditedName(status.name || "");
                      }}
                    >
                      Edit
                    </button>
                  ) : (
                    <span className="policy-status-readonly-label">
                      Locked
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return (
      <div className="policy-status-empty-state">
        {loading ? "Loading statuses..." : emptyMessage}
      </div>
    );
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page settings-page-modern">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">Settings</p>
            <h1 className="sales-title">Policy / Opportunity Statuses</h1>
            <p className="sales-subtitle">
              Add and manage status options used for policies and opportunities.
            </p>
          </div>

          <div className="sales-header-actions">
            <Link
              className="sales-secondary-btn"
              to="/settings?tab=Data%20%26%20Customization"
              style={{ textDecoration: "none" }}
            >
              ← Back to Data & Customization
            </Link>

            <button
              type="button"
              className="sales-secondary-btn"
              onClick={loadStatuses}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {(error || success) && (
          <div
            className={`sales-section-card policy-status-message-card ${
              error ? "policy-status-message-error" : "policy-status-message-success"
            }`}
          >
            {error || success}
          </div>
        )}

        <div className="policy-status-grid">
          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>Add Statuses</h2>
              <p>Choose the status type and enter one status per line.</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="sales-form-fields">
                <div className="sales-field sales-field-full">
                  <label>Status Type</label>
                  <select
                    value={type}
                    onChange={(event) => setType(event.target.value)}
                  >
                    <option value="policy">Policy</option>
                    <option value="opportunity">Opportunity</option>
                  </select>
                </div>

                <div className="sales-field sales-field-full">
                  <label>Status Names</label>
                  <textarea
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={
                      type === "policy"
                        ? "Active\nTerminated\nReplaced"
                        : "New\nSubmitted\nApproved"
                    }
                    rows={8}
                  />
                </div>
              </div>

              <div className="settings-inline-actions policy-status-actions">
                <button
                  type="submit"
                  className="sales-primary-btn"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Add Status(es)"}
                </button>

                <button
                  type="button"
                  className="sales-secondary-btn"
                  onClick={() => {
                    setName("");
                    clearMessages();
                  }}
                  disabled={saving}
                >
                  Clear
                </button>
              </div>
            </form>
          </div>

          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>Existing Statuses</h2>
              <p>Search, edit, or delete custom statuses.</p>
            </div>

            <div className="sales-form-fields policy-status-search-row">
              <div className="sales-field sales-field-full">
                <label>Search</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search statuses..."
                />
              </div>
            </div>

            <div className="policy-status-table-section">
              <div className="policy-status-table-title">
                <h3>Policy Statuses</h3>
                <span>{policyStatuses.length}</span>
              </div>

              <div className="policy-status-table-wrap">
                {renderStatusTable(policyStatuses, "No policy statuses found.")}
              </div>
            </div>

            <div className="policy-status-table-section">
              <div className="policy-status-table-title">
                <h3>Opportunity Statuses</h3>
                <span>{opportunityStatuses.length}</span>
              </div>

              <div className="policy-status-table-wrap">
                {renderStatusTable(opportunityStatuses, "No opportunity statuses found.")}
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedStatus && (
        <div
          className="modal-overlay"
          onClick={() => {
            setSelectedStatus(null);
            setEditedName("");
          }}
        >
          <div
            className="modal policy-status-edit-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>Edit Status</h3>

            <div className="sales-field sales-field-full">
              <label>Status Name</label>
              <input
                type="text"
                value={editedName}
                onChange={(event) => setEditedName(event.target.value)}
                placeholder="Status name"
              />
            </div>

            <div className="modal-actions policy-status-modal-actions">
              <button
                type="button"
                className="modal-button save"
                onClick={handleSaveEdit}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>

              <button
                type="button"
                className="modal-button delete"
                onClick={handleDelete}
                disabled={saving}
              >
                Delete
              </button>

              <button
                type="button"
                className="modal-button neutral"
                onClick={() => {
                  setSelectedStatus(null);
                  setEditedName("");
                }}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PolicyStatus;